import { ColumnDef, Row } from '@tanstack/react-table';
import { IconButton, Tag, TruncatableText } from 'Atoms/index.ts';
import { ShortUser } from 'models/types.ts';
import { Table } from 'Molecules/index.ts';
import { UserAvatar } from 'Organisms/index.ts';
import { ArrowRightIcon } from 'Tokens/Icons/Direction.tsx';
import { Typography } from 'Tokens/index.ts';
import { Box, Checkbox, HStack, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { DescriptionIcon, HashIcon, MinusIcon } from 'Tokens/Icons/Data';
import { CheckIcon, LoaderIcon } from 'Tokens/Icons/Status';
import { uniq } from 'lodash';
import { EmptyOwner } from 'Atoms/EmptyOwner';

export enum TaskListStatus {
  NOT_STARTED = 'Not Started',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
}

export type Task = {
  id: string;
  owner?: ShortUser;
  title: string;
  reference: string;
  status: TaskListStatus;
  metricType: string;
  metricTypeCounts?: {
    narrative: number;
    quantitative: number;
  };
  dueDate?: Date;
  link?: string;
  addedCount?: number;
  totalCount?: number;
  completedCount?: number;
  inProgressCount?: number;
  notStartedCount?: number;
  standardTitle?: string;
  drOrder?: number;
  reportingUnitName?: string;
  taskIds?: string[];
};

export type TaskListRow = { task: Task | null; subtitle: string | null };

export const TaskListTable = ({
  data,
  selectedTasks,
  setSelectedTasks,
  isHomepage,
}: {
  data: TaskListRow[];
  selectedTasks?: string[];
  setSelectedTasks?: (val: string[]) => void;
  isHomepage?: boolean;
}) => {
  const navigate = useNavigate();
  const handleSelectTask = (id: string, taskRow: Row<TaskListRow>) => {
    if (selectedTasks && setSelectedTasks) {
      if (taskRow.original.subtitle) {
        const slicedData = data.slice(taskRow.index + 1, data.length);
        const rowsToSelect = slicedData
          .slice(
            0,
            slicedData.findIndex((row) => !!row.subtitle) === -1
              ? data.length
              : slicedData.findIndex((row) => !!row.subtitle)
          )
          .map((row) => row.task?.id ?? '');

        if (selectedTasks.includes(id))
          setSelectedTasks(
            selectedTasks.filter((std) => std !== id && !rowsToSelect.includes(std))
          );
        else {
          setSelectedTasks(uniq([...selectedTasks, id, ...(rowsToSelect ?? [])]));
        }
      } else if (selectedTasks.includes(id)) {
        const parentRowIndex = data
          .slice(
            0,
            data.findIndex((row) => row.task?.id === id)
          )
          .findLastIndex((row) => !!row.subtitle);
        setSelectedTasks(selectedTasks.filter((std) => std !== id && std !== `${parentRowIndex}`));
      } else setSelectedTasks([...selectedTasks, id]);
    }
  };

  const columns: ColumnDef<TaskListRow>[] = [
    {
      id: 'checkbox',
      accessorKey: 'checkbox',
      cell: ({ row }) => {
        return (
          <HStack
            w="28px"
            height={row.original.subtitle ? '48px' : ''}
            mt={row.original.subtitle ? '25px' : ''}
          >
            <Checkbox
              isChecked={selectedTasks?.includes(row.original.task?.id ?? `${row.index}`)}
              onChange={(e) => {
                e.stopPropagation();
                handleSelectTask(row.original.task?.id ?? `${row.index}`, row);
              }}
            />
          </HStack>
        );
      },
    },
    {
      id: 'owner',
      accessorKey: 'owner',
      cell: ({ row }) => {
        return row.original.subtitle ? (
          <HStack w="28px" overflow="visible">
            <HStack minW="1000px" height="48px" alignContent="center" mt={'26px'}>
              <Typography
                variant="overline"
                color="text.hint"
                whiteSpace="nowrap"
                noOfLines={1}
                w="100%"
              >
                {row.original.subtitle}
              </Typography>
            </HStack>
          </HStack>
        ) : row.original.task ? (
          <HStack w="28px">
            {row.original.task.owner ? (
              <UserAvatar size="xs" user={row.original.task.owner} />
            ) : (
              <EmptyOwner size="20px" />
            )}
          </HStack>
        ) : null;
      },
      meta: { width: '36px' },
    },
    {
      accessorKey: 'title',
      cell: ({ row }) =>
        !row.original.subtitle && row.original.task ? (
          <VStack spacing={0} alignItems={'start'} pl={isHomepage ? '8px' : ''}>
            <TruncatableText text={row.original.task.title} variant="bodyStrong" />
            {row.original.task.reportingUnitName && (
              <Typography variant="detail">{row.original.task.reportingUnitName}</Typography>
            )}
          </VStack>
        ) : (
          <></>
        ),
      meta: { width: '42%' },
    },
    {
      accessorKey: 'metricTypeCounts',
      cell: ({ row }) => {
        if (!row.original.task) return null;
        const metricTypeCounts = row.original.task.metricTypeCounts;

        if (
          !metricTypeCounts ||
          (metricTypeCounts.narrative === 0 && metricTypeCounts.quantitative === 0)
        ) {
          return (
            <HStack justifyContent="flex-end" marginInline="6px">
              <Tag variant="default" size="xs">
                <Typography variant="detailStrong">No metrics</Typography>
              </Tag>
            </HStack>
          );
        }
        return (
          <HStack justifyContent="flex-end" marginInline="6px" gap="2px" w="90px">
            {!!metricTypeCounts.narrative && (
              <Tag
                variant="default"
                size="xs"
                tagTooltip="Narrative"
                leftIcon={<DescriptionIcon />}
              >
                <Typography variant="detailStrong">{metricTypeCounts.narrative}</Typography>
              </Tag>
            )}
            {!!metricTypeCounts.quantitative && (
              <Tag variant="default" size="xs" tagTooltip="Quantitative" leftIcon={<HashIcon />}>
                <Typography variant="detailStrong">{metricTypeCounts.quantitative}</Typography>
              </Tag>
            )}
          </HStack>
        );
      },
      meta: { width: '10%' },
    },
    {
      accessorKey: 'reference',
      cell: ({ row }) =>
        row.original.task ? (
          <TruncatableText
            text={row.original.task.reference}
            variant="bodyStrong"
            marginInline="6px"
            minW="80px"
            color="text.hint"
          />
        ) : null,
      meta: { width: '8%' },
    },
    {
      accessorKey: 'dueDate',
      cell: ({ row }) => {
        if (!row.original.task) return null;
        if (row.original.task.dueDate === undefined || row.original.task.dueDate === null) {
          return (
            <Typography variant="body" w="100px" ml="2px">
              No due date
            </Typography>
          );
        } else {
          const date = new Date(row.original.task.dueDate);
          const now = new Date();
          date.setHours(0, 0, 0, 0);
          now.setHours(0, 0, 0, 0);
          const diffMs = date.getTime() - now.getTime();
          const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
          if (diffDays === 0) {
            return (
              <Typography variant="body" w="100px" ml="2px">
                Due today
              </Typography>
            );
          } else if (diffDays > 0) {
            return (
              <Typography variant="body" w="100px" ml="2px">
                {date.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
            );
          } else {
            return (
              <Typography variant="body" color="text.critical" w="100px" ml="2px">
                {date.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
            );
          }
        }
      },
      meta: { width: '115px' },
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => {
        if (!row.original.task) return null;
        const status = row.original.task.status;
        const inProgressCount = row.original.task.inProgressCount ?? 0;
        const notStartedCount = row.original.task.notStartedCount ?? 0;
        const completedCount = row.original.task.completedCount ?? 0;
        const notCompletedCount = inProgressCount + notStartedCount;

        return (
          <HStack gap="4px" justifyContent="end" marginInline="6px" alignSelf="end">
            <Tag
              variant="ghost"
              leftIcon={<MinusIcon color="currentcolor" />}
              title={String(notStartedCount)}
              size="xs"
              tagTooltip="Not started"
              w="39px"
              paddingY="2px"
              gap="0px"
              textWidth="18px"
              maxWidthInPixels={18}
              opacity={notStartedCount ? '1' : '0.4'}
              padding="2px 3px"
            />
            <Tag
              variant="info"
              title={String(inProgressCount)}
              leftIcon={<LoaderIcon color="currentcolor" />}
              tagTooltip="In progress"
              paddingY="2px"
              w="39px"
              textWidth="18px"
              gap="0px"
              opacity={inProgressCount ? '1' : '0.4'}
              padding="2px 3px"
              size="xs"
            />
            <Tag
              variant="success"
              leftIcon={<CheckIcon color="currentcolor" />}
              title={String(completedCount)}
              tagTooltip="Completed"
              paddingY="2px"
              w="39px"
              textWidth="18px"
              gap="0px"
              opacity={completedCount ? '1' : '0.4'}
              padding="2px 3px"
              size="xs"
            />
          </HStack>
        );
      },
      meta: { width: '15%' },
    },
    {
      accessorKey: 'link',
      cell: ({ row }) =>
        row.original.task ? (
          <IconButton
            aria-label={''}
            variant="ghost"
            icon={<ArrowRightIcon />}
            onClick={() => {
              const taskOwnerFilter = `?taskUserId=${row.original.task?.owner?.id}`;
              const taskUnassignedFilter = `?taskFilter=unassigned`;
              navigate(
                `/${row.original.task?.link ?? ''}${row.original.task?.owner ? taskOwnerFilter : taskUnassignedFilter}`,
                {
                  replace: true,
                }
              );
            }}
          />
        ) : null,
      meta: { width: '5%' },
    },
  ];

  const filtereColumns = columns.filter((col) =>
    !!selectedTasks ? true : col.id !== 'checkbox' && col.id !== 'owner'
  );

  return (
    <Table
      columns={filtereColumns}
      pageSize={1000000}
      data={data}
      showHeader={false}
      topRowBorder={isHomepage}
      bottomRowBorder={!isHomepage}
      cellPadding="0px"
      rowHeight="32px"
      conditionalCellProps={(cell) => (cell.id === 'checkbox' ? { borderBottom: 'none' } : {})}
    />
  );
};
